import React, {createContext, PropsWithChildren, useCallback, useContext, useEffect} from 'react';
import {CloseModalEvent, CloseModalReason} from '../base-modal/CloseModalEvent';
import {ModalAdminPaymentRequest} from './admin-payment-request-modal';
import {ApiRequestException} from '../../../api/axios-instance';
import {useIntl} from 'react-intl';
import {useAdvancedState} from '../../../hooks/use-advanced-state';
import {ValidationErrorsType} from '../../../utils/utils';
import {useLoading} from '../../../hooks/use-loading';
import {EntityId} from '../../../api/base/BaseEntity';
import {toast} from 'react-toastify';
import {usePaymentsRequestsApi} from '../../../hooks/apis/use-payments-requests-api';
import {useSelectApi} from '../../../hooks/use-select-api';
import {FinancialAccountType} from '../../../api/DTOs/FinancialAccount';
import {EXCEPTION_TYPE} from '../../../api/exceptions/BaseException';
import {ValidationException} from '../../../api/exceptions/ValidationException';
import {useAccountsApi} from '../../../hooks/apis/use-accounts-api';
import {SelectOptions} from '../../Inputs/InputSelect';
import {Currency} from '../../../api/DTOs/Transaction';

interface IModalPaymentRequestProps {
  modalPaymentRequestVisible: boolean;

  showPaymentRequestModal(): Promise<CloseModalEvent<null>>;
}

export type ModalPaymentRequestField = {
  contractor_id: EntityId;
  contractors: SelectOptions;
  balance: any;
  error: string;
  validationErrors: ValidationErrorsType;
  value: string | number;
  visible: boolean;
};

// @ts-ignore
const ModalAdminPaymentRequestContext = createContext<IModalPaymentRequestProps>();

let closeResolver: ((data: CloseModalEvent<null>) => unknown) | null = null;
export const ModalAdminPaymentRequestProvider: React.FC = ({children}: PropsWithChildren<unknown>) => {
  const intl = useIntl();
  const {selectsOptions, fetchSelectValuesByKey} = useSelectApi();
  const api = usePaymentsRequestsApi();
  const accountsApi = useAccountsApi();
  const [loadings, startLoading, stopLoading] = useLoading({createRequest: false, balance: false});
  const [state, , updateState, resetState] = useAdvancedState<ModalPaymentRequestField>({
    visible: false,
  });

  useEffect(() => {
    if (state.contractor_id != null) {
      fetchBalance(state.contractor_id).then();
    }
  }, [state.contractor_id]);

  const fetchContractors = useCallback(() => {
    fetchSelectValuesByKey('contractors', {filters: {mc_pay: 'HAS_CREDENTIALS'}}).then();
  }, []);

  useEffect(() => {
    if (selectsOptions?.contractors != null) {
      updateState({contractors: selectsOptions.contractors});
    }
  }, [selectsOptions?.contractors]);

  const fetchBalance = useCallback(async (contractorId: EntityId) => {
    try {
      startLoading('balance');
      const {items} = await accountsApi.getAll({
        filters: {
          contractor_id: contractorId,
          type: FinancialAccountType.INNER,
          currency: Currency.USD,
        },
      });

      if (items.length > 0) {
        updateState({
          balance: {
            balance: items[0].balance,
            unconfirmedBalance: items[0].waitingConfirmBalance,
            withdrawalAvailableBalance: items[0].withdrawalAvailableBalance,
          },
        });
      }
    } catch (e) {
      updateState({balance: null});
      const err = e as ApiRequestException;
      toast.error(e.message || err.errorMessage || intl.formatMessage({id: 'UNEXPECTED_ERROR'}));
    } finally {
      stopLoading('balance');
    }
  }, []);

  const showModal = async () => {
    updateState({
      visible: true,
    });

    fetchContractors();

    return new Promise<CloseModalEvent<null>>(resolve => {
      closeResolver = resolve;
    });
  };

  const handleHideModal = () => {
    resetState();
    if (closeResolver) {
      closeResolver({reason: CloseModalReason.HIDE});
      closeResolver = null;
    }
  };

  const handleOkClick = async () => {
    try {
      updateState({error: null, validationErrors: null});
      await api.create(state);
      if (closeResolver) {
        closeResolver({reason: CloseModalReason.OK});
        closeResolver = null;
      }
      await handleHideModal();
    } catch (e) {
      const err = e as ApiRequestException;
      if (err.errorType === EXCEPTION_TYPE.VALIDATION_EXCEPTION) {
        updateState({
          validationErrors: (err.innerException as ValidationException).error_data.messages,
        });
      } else {
        updateState({error: err.errorMessage || intl.formatMessage({id: 'UNEXPECTED_ERROR'})});
      }
    }
  };

  const value: IModalPaymentRequestProps = {
    modalPaymentRequestVisible: state.visible ?? false,
    showPaymentRequestModal: showModal,
  };

  return (
    <ModalAdminPaymentRequestContext.Provider value={value}>
      {children}
      <ModalAdminPaymentRequest
        loadings={loadings}
        state={state}
        updateState={updateState}
        onOkClick={handleOkClick}
        onHide={handleHideModal}
      />
    </ModalAdminPaymentRequestContext.Provider>
  );
};

export const useModalAdminPaymentRequest = () => {
  return useContext(ModalAdminPaymentRequestContext);
};
